import * as Yup from 'yup'
import type { Gender, PersonNameDto } from '../../config/contactConfig'
import type { BasePartyMutationDto } from '../../components/Form/Party/PartyForm'
import { AttributeDefinitionDto, getAttributeValidationSchema } from '../../hooks/useAttributes'
import { formatMultipleEmailAddresses, yupEmailRegex } from '../../helpers/regexHelper'

/**
 * Shared mutation DTOs + validation-schema factories for the v9 party edit/add screens (HAM-3911).
 *
 * Lifted verbatim from the legacy NaturalPersonForm/LegalPersonForm so no field, rule or message is
 * lost. The schemas are exposed as `translate`-taking factories (never module scope) so their
 * messages localise, per the client i18n rule.
 */

export enum IdentificationType {
    Passport = 'PASSPORT',
    IdCard = 'ID_CARD',
    DriversLicense = 'DRIVERS_LICENSE',
    Other = 'OTHER',
}

export type NaturalPersonMutationDto = BasePartyMutationDto & {
    gender: Gender
    name: PersonNameDto
    salutation: string
    attentionOf: string
    phoneMobile: string
    dateOfBirth: string
    identificationNumber: string
    identificationPersonalNumber: string
    identificationType: IdentificationType
    identificationDateOfIssue: string
    identificationDateOfExpiry: string
    identificationAuthority: string
    identificationPlaceOfIssue: string
    identificationCountryOfIssue: string
}

export type LegalPersonMutationDto = BasePartyMutationDto & {
    name: string
    legalStructure: string
    poBoxAddressStreet?: string
    poBoxAddressNumber?: string
    poBoxAddressPostalCode?: string
    poBoxAddressLocality?: string
    poBoxAddressMunicipality?: string
    poBoxAddressCountry?: string
    financeTermInvoice: number
    financeTermPayment: number
    financeEmail: string
    vatNumber: string
    cocNumber: string
    cocBranchNumber?: string
    fileReferenceRequired?: boolean
    reference?: string
}

/** Every value is valid on its own, but each address part requires a country (and vice versa). */
const addressCountryTest = (prefix: 'address' | 'poBoxAddress') => (value: string, context: Yup.TestContext) =>
    !!value?.trim() ||
    !!value ===
        !!(
            context.parent[`${prefix}Street`]?.trim() ||
            context.parent[`${prefix}Number`]?.trim() ||
            context.parent[`${prefix}PostalCode`]?.trim() ||
            context.parent[`${prefix}Locality`]?.trim()
        )

/** Accepts empty, or one-or-more `;`/`,`-separated addresses that each match the email regex. */
const multipleEmailsTest = (values: string) =>
    !values ||
    formatMultipleEmailAddresses(values)?.every(value => {
        const result = value?.trim().match(yupEmailRegex)
        return result?.length > 0
    })

/**
 * A CRM relation is what a dossier hangs its documents and its contact under, so it needs a
 * responsible user. Only relations: an ordinary party has no account manager to fill in.
 */
const accountManagerRule = (translate: (key: string) => string) =>
    Yup.string()
        .nullable()
        .label(translate('party.edit.crm.accountmanagerid'))
        .when('crmRelation', { is: true, then: schema => schema.required() })

/**
 * For a CRM relation the short name is what identifies the party throughout the application - it
 * names the SharePoint folder and prefixes the dossier name - so there it is mandatory. Any other
 * party (a supplier, the opposing party's lawyer) is never shown by it, so it stays optional.
 */
const shortNameRule = (translate: (key: string) => string, labelKey: string) =>
    Yup.string()
        .nullable()
        .label(translate(labelKey))
        .when('crmRelation', { is: true, then: schema => schema.required() })

/**
 * The document location is resolved into an intent by FolderPathField, not typed into a value, so
 * that intent is what the schema checks: without one there is nothing to create on save and the
 * relation would end up without a folder (HAM-4343).
 */
const documentRootRule = (translate: (key: string) => string) =>
    Yup.mixed()
        .nullable()
        .label(translate('base.folderpath.label'))
        .when('crmRelation', { is: true, then: schema => schema.required() })

export const getNaturalPersonValidationSchema = (
    translate: (key: string) => string,
    attributeDefinitions: AttributeDefinitionDto[],
): Yup.ObjectSchema<any> =>
    Yup.object().shape({
        gender: Yup.string().label(translate('party.edit.person.gender')).required(),
        shortName: shortNameRule(translate, 'party.edit.person.shortname'),
        accountManagerId: accountManagerRule(translate),
        documentRootIntent: documentRootRule(translate),
        name: Yup.object().shape({
            last: Yup.string().label(translate('party.edit.person.last')).required(),
        }),
        email: Yup.string()
            .nullable()
            .test({ message: translate('base.form.email.warning'), test: multipleEmailsTest })
            .label(translate('party.edit.contact.email')),
        addressCountry: Yup.string()
            .nullable()
            .test({ test: addressCountryTest('address') })
            .label(translate('party.edit.address.country')),
        attributes: getAttributeValidationSchema(translate, attributeDefinitions),
    })

export const getLegalPersonValidationSchema = (
    translate: (key: string) => string,
    attributeDefinitions: AttributeDefinitionDto[],
): Yup.ObjectSchema<any> =>
    Yup.object().shape({
        name: Yup.string().label(translate('party.edit.company.name')).required(),
        shortName: shortNameRule(translate, 'party.edit.company.shortname'),
        accountManagerId: accountManagerRule(translate),
        documentRootIntent: documentRootRule(translate),
        cocNumber: Yup.string()
            .nullable()
            .label(translate('party.edit.company.cocNumber'))
            .test({ message: translate('party.edit.company.cocNumber.warning'), test: value => !value || /^\d{8}$/.test(value.trim()) }),
        cocBranchNumber: Yup.string()
            .nullable()
            .label(translate('party.edit.company.cocBranchNumber'))
            .test({ message: translate('party.edit.company.cocBranchNumber.warning'), test: value => !value || /^\d{12}$/.test(value.trim()) }),
        email: Yup.string()
            .nullable()
            .label(translate('party.edit.contact.email'))
            .test({ message: translate('base.form.email.warning'), test: multipleEmailsTest }),
        financeEmail: Yup.string()
            .nullable()
            .label(translate('party.edit.finance.financeemail'))
            .test({ message: translate('base.form.email.warning'), test: multipleEmailsTest }),
        addressCountry: Yup.string()
            .nullable()
            .test({ test: addressCountryTest('address') })
            .label(translate('party.edit.address.country')),
        poBoxAddressCountry: Yup.string()
            .nullable()
            .label(translate('party.edit.address.country'))
            .test({ test: addressCountryTest('poBoxAddress') }),
        attributes: getAttributeValidationSchema(translate, attributeDefinitions),
    })
